import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
// import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { TP, PRIVACY_ATTRIBUTE_IDS } from '../../../../../constants/consts';
import { selectChosenProfileReviews, selectUpdateMediaLoading } from '../../../selectors';
import { createEntity, deleteEntity, updateOne } from '../../../../App/actions';
import { REVIEWS } from '../../../../../constants/endpointConstants';
import {
  addReviewAction,
  deleteReviewAction,
  getChosenProfileDataAction,
  getChosenProfileReviewsAction,
  updateReviewAction,
} from '../../../actions';
import AddReviewDetailsForm from '../AddReviewDetailsForm';
import FbCircularProgress from '../../../../../components/FbCircularProgress';
import { ArtistReviewCard } from '../../../../../components/EditComponents/ArtistReviewCard';
import ActionStatusModal from '../../../../../components/ActionStatusModal';
import TextButton from '../../../../../components/TextButton';
import Button from '../../../../../components/Button';
import { useTranslation } from '../../../../../src/i18n';
import EditMediaLayout from '../../../../../components/EditComponents/EditMediaLayout';

const useStyles = makeStyles(theme => ({
  card: {
    marginBottom: theme.spacing(2),
  },
}));

const ReviewsTab = ({ title, subtitle, profile, privacyOptions }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const reviews = useSelector(selectChosenProfileReviews());
  const reviewUpdating = useSelector(selectUpdateMediaLoading());

  const [isEditModalOpened, setEditModalState] = React.useState(false);
  const [editModalRecord, setEditModalRecord] = React.useState(null);
  const [reviewToDelete, setReviewToDelete] = React.useState(null);

  React.useEffect(() => {
    dispatch(getChosenProfileReviewsAction({ id: profile?.id, query: {} }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEditRecordModal = chosenRecord => {
    setEditModalState(true);
    setEditModalRecord(chosenRecord);
  };

  const closeEditRecordModal = () => {
    setEditModalState(false);
    setEditModalRecord(null);
  };

  const updateRecord = data => {
    if (data) {
      const dataToSend = {
        ...editModalRecord,
        ...data,
        // production: { id: 1 },
      };
      if (editModalRecord?.id) {
        dispatch(
          updateOne({
            endpoint: REVIEWS(profile?.id),
            id: editModalRecord?.id,
            sagaRoutine: updateReviewAction,
            params: dataToSend,
            callback: () => closeEditRecordModal(),
          }),
        );
      } else {
        dispatch(
          createEntity({
            endpoint: REVIEWS(profile?.id),
            sagaRoutine: addReviewAction,
            params: { ...dataToSend, privacyAttribute: { id: PRIVACY_ATTRIBUTE_IDS.PUBLIC } },
            callback: () => {
              dispatch(getChosenProfileDataAction({ id: profile?.id }));
              closeEditRecordModal();
            },
          }),
        );
      }
    }
  };

  const updatePrivacy = (review, option) => {
    dispatch(
      updateOne({
        endpoint: REVIEWS(profile?.id),
        id: review?.id,
        sagaRoutine: updateReviewAction,
        params: { ...review, privacyAttribute: { id: option?.value } },
      }),
    );
  };

  const deleteReview = () => {
    dispatch(
      deleteEntity({
        endpoint: REVIEWS(profile?.id),
        sagaRoutine: deleteReviewAction,
        id: reviewToDelete?.id,
        callback: () => {
          dispatch(getChosenProfileDataAction({ id: profile?.id }));
          closeConfirmationModal();
        },
      }),
    );
  };

  const closeConfirmationModal = () => {
    setReviewToDelete(null);
  };

  return (
    <>
      <EditMediaLayout
        title={title}
        subtitle={subtitle}
        addButtonClick={() => openEditRecordModal({})}
        buttonTitle={`edit:${TP}.FN_EDITARTIST_ADD_REVIEWS`}
        isEmpty={reviews?.data?.length === 0 && reviews?.loadedOnce}
        emptyMessage={`edit:${TP}.FN_EDITARTIST_NO_REVIEWS`}
      >
        <section>
          {reviews?.loading || !reviews?.loadedOnce ? (
            <FbCircularProgress />
          ) : (
            reviews?.data?.map(r => (
              <ArtistReviewCard
                review={r}
                key={r?.id}
                className={classes.card}
                privacyOptions={privacyOptions}
                onAddDetails={() => openEditRecordModal(r)}
                onSelect={option => updatePrivacy(r, option)}
                onDelete={() => setReviewToDelete(r)}
              />
            ))
          )}
        </section>
      </EditMediaLayout>
      {isEditModalOpened && (
        <AddReviewDetailsForm
          isOpened={isEditModalOpened}
          onClose={closeEditRecordModal}
          entity={editModalRecord}
          submitFunc={updateRecord}
          profile={profile}
          isLoading={reviewUpdating}
        />
      )}
      {reviewToDelete && (
        <ActionStatusModal
          isOpened
          onClose={closeConfirmationModal}
          type="warning"
          title={t(`${TP}.FN_DELETE_ARTIST_REVIEW_CONFIRMATION`)}
          buttons={[
            <TextButton onClick={deleteReview}>{t(`${TP}.m_DELETE`)}</TextButton>,
            <Button onClick={closeConfirmationModal}>{t(`${TP}.m_CANCEL`)}</Button>,
          ]}
        >
          {reviewUpdating && <FbCircularProgress />}
        </ActionStatusModal>
      )}
    </>
  );
};

ReviewsTab.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  profile: PropTypes.object.isRequired,
  privacyOptions: PropTypes.array.isRequired,
};

ReviewsTab.defaultProps = {
  title: '',
  subtitle: '',
};

export default memo(ReviewsTab);
